/* eslint-disable indent */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  applyProgressiveStyleSlotsToDesignGroups,
  getReviewOrderItemId,
} from '../helpers/applyProgressiveStyleSlots.utils';
import type { IProgressiveStyleSlots } from '../helpers/applyProgressiveStyleSlots.utils';
import { buildDesignImagesFromItem } from '../helpers/buildDesignImagesFromItem.utils';
import {
  getDesignGroupIdFromGroup,
  getDesignGroupLabel,
  getRemainingItemsAfterRemoval,
  reindexRecordAfterGroupRemoval,
  reorderDesignGroupsAfterRemoval,
} from '../helpers/storeReviewRemove.utils';
import {
  getFundraiserDonationPurchaseMessage,
  getInitialStoreReviewDonationAmounts,
  getItemFinalPriceWithDonation,
  getStoreReviewBreakpointDisplayPrice,
  hasPositiveDonationAmount,
  shouldShowItemFinalPrice,
  type StoreReviewStoreType,
} from '../helpers/storeReviewDonationValidation.utils';
import { groupStoreReviewItemsByProduct } from '../helpers/groupStoreReviewItemsByProduct.utils';
import { parseStoreReviewProductInfoBullets } from '../helpers/storeReviewProductInfo.utils';
import StoreReviewItemGalleryFooter from '../StoreReviewAi/StoreReviewItemGalleryFooter/StoreReviewItemGalleryFooter';
import StoreReviewProgressiveModal from '../StoreReviewAi/StoreReviewProgressiveModal/StoreReviewProgressiveModal';
import StoresDesignImagesList from '../StoreReviewAi/StoresDesignImagesList/StoresDesignImagesList';
import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import { useStoreCheckoutUiVariationOptional } from '../context/StoreCheckoutUiVariationContext';
import StoreCheckoutBackIcon from '../StoreCheckoutHeader/StoreCheckoutBackIcon';
import { useEditModeSafe } from '../context/EditModeContext';
import {
  isErrorStateStoreCheckoutUiVariation,
  isSingleImageGenerationStoreCheckoutUiVariation,
  showsStoreCheckoutAiImageGenerationActions,
  STORE_MESSAGES,
  STORE_NAVIGATION,
  STORE_REVIEW_DONATION_INPUT_UNIT_SUFFIX,
  STORE_REVIEW_FINAL_PRICE_LABEL,
  STORE_DONATION_MARKUP_AMOUNT_LIMITS,
  getStoreReviewPerItemAdjustmentLabel,
  STORE_REVIEW_PRODUCT_TITLE_LABEL,
  STORE_REVIEW_PRODUCT_INFO_EMPTY_MESSAGE,
  STORE_REVIEW_PRODUCT_INFO_LABEL,
  getStoreDesignMinimumSubtitle,
  STORE_REVIEW_PRICE_PER_ITEM_UNIT_SUFFIX,
  STORES_FORM_MIXPANEL_SECTIONS,
} from '../constants/storeCheckout.constants';
import {
  getStoreReviewDefaultMinimumQuantity,
  getStoreReviewDesignGroupKey,
  getStoreReviewBreakpointUnitPrice,
  getStoreReviewItemQuotedPrice,
  getStoreReviewPricingBreakpoints,
  inferStoreReviewPricingModelFromGroup,
  resolveStoreReviewGroupMinimumQuantity,
  resolveStoreReviewPricingModelFromGroup,
} from '../helpers/storeReviewPricing.utils';
import type { StoreAiPreviewVibe } from '../constants/storeCheckoutAi.constants';
import {
  buildStoreProductTitleUpdatePayload,
  getStoreReviewProductTitleInputValue,
} from '../helpers/storeCheckoutProductTitleUpdate.utils';
import {
  buildStorePricingSaveKey,
  buildStorePricingUpdatePayload,
} from '../helpers/storeCheckoutPricingUpdate.utils';
import {
  buildStoreDonationMarkupUpdatePayload,
  isStoreDonationMarkupStoreType,
  normalizeStoreDonationMarkupOnBlur,
  preventStoreDonationMarkupNegativeKey,
  sanitizeStoreDonationMarkupInput,
  STORE_DONATION_MARKUP_UPDATE_DEBOUNCE_MS,
} from '../helpers/storeCheckoutDonationMarkup.utils';
import type { StoreCartMoqShippingLane } from '../helpers/storeCheckoutMoqShipping.utils';
import type { StoreReviewPricingModel } from '../helpers/storeReviewPricing.utils';
import {
  hasAllDesignGroupsPricingSelected,
  hasAllStoreReviewProductTitles,
} from '../helpers/storeCheckoutLaunchValidation.utils';
import { areStoreReviewItemsQuotedAtQuantity } from '../helpers/storeReviewQuote.utils';
import {
  STORE_FLOW_PRIMARY_BACKGROUND,
  STORE_FLOW_PRIMARY_BORDER,
  STORE_FLOW_PRIMARY_COLOR,
  STORE_FLOW_BODY_FONT_FAMILY,
} from '@/constants/StoreFlow';
import { getStoreCartDesignGroupMoqRequestKey } from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import {
  DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS,
  STORE_CART_MOQ_LOADING_ARIA_LABEL,
  STORE_CART_PRICE_LOADING_ARIA_LABEL,
} from '@/modules/Cart/StoreCart/storeCart.constants';
import {
  getStoreCartDesignGroupApiStatus,
  isStoreCartDesignGroupMoqPending,
  shouldShowStoreCartDesignGroupMoqTiers,
} from '@/modules/Cart/StoreCart/helpers/storeCartSetup.utils';
import { useStoreCartDesignGroupMoqs } from '@/hooks/useStoreCartDesignGroupMoqs';
import { useStoreReviewBreakdownPricing } from '@/hooks/useStoreReviewBreakdownPricing';
import { useStoreReviewDesignGroupQuotes } from '@/hooks/useStoreReviewDesignGroupQuotes';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';
import { captureStoresFormSectionCompleted } from '@/mixpanel/MixpanelEvents';
import useViewport from '@/common/context/ViewPort/useViewPort';
import ViewportContext from '@/common/context/ViewPort/ViewPortContext';
import { ViewPort } from '@/common/context/ViewPort/ViewPort';
import { Text } from '@/components/Text/Text';
import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { ToolTip } from '@/components/Tooltip/Tooltip';
import Overlay from '@/components/Overlay/Overlay';
import {
  capturePageScrollSnapshot,
  restorePageScrollSnapshot,
  type IPageScrollSnapshot,
} from '@/components/Overlay/modalScrollLock.utils';
import { currencyFormat } from '@/utilities/CommonUtils';
import { isDesignRejected } from '@/utilities/LicensingUtils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import { getPrintTypeList, getDecoratingTypeList } from '@/utilities/PrintTypeDecorUtils';
import type { IDecoratingItem, IPrintTypeItem } from '@/common/interfaces/print-decor.interface';
import PrintDecorComponent from '@/components/PrintDecorComponent/PrintDecorComponent';
import PatternColorblockPills from '@/components/PatternColorblockPills/PatternColorblockPills';
import { buildPatternColorblockPills } from '@/utilities/PatternColorblockPillUtils';
import DesignColorOptions from '@/modules/Designs/DesignColorOptions/DesignColorOptions';
import type { IDesignGroupColorOption } from '@/common/interfaces/design.interface';
import {
  useDeleteApiServerStoreDesignGroupMutation,
  useDeleteApiServerStoreDesignsMutation,
  useUpdateStoreMutation,
  useUpdateStoreDonationMarkupMutation,
} from '@/api/StoreQuery';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import './StoreReviewOrderStep.scss';
import './store-pricing-section.scss';

export type { DesignGroup };

/**
 * Below this width the review step renders the mobile layout. The SCSS mobile
 * breakpoints kick in at 767.98px, so the JS layout must match to avoid the
 * broken tabSmall (620–767px) state.
 */
const MOBILE_LAYOUT_MAX_WIDTH = 768;

interface StoreReviewOrderStepProps {
  items: ICartItemDetail[];
  onDesignGroupsChange?: (groups: DesignGroup[]) => void;
  onDonationAmountsChange?: (amounts: Record<string, string>) => void;
  donationAmounts?: Record<string, string>;
  onItemsChange?: (items: ICartItemDetail[]) => void;
  storeId?: string | number | null;
  storeType?: 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | '';
  charityName?: string;
  isReadOnly?: boolean;
  isItemTitleEditable?: boolean;
  isPricingReadOnly?: boolean;
  moqShippingLane?: StoreCartMoqShippingLane;
  onShippingLaneRequoteComplete?: (quotedItems: ICartItemDetail[]) => void;
}

const StoreReviewOrderStep: React.FC<StoreReviewOrderStepProps> = ({
  items,
  onDesignGroupsChange,
  onDonationAmountsChange,
  donationAmounts: donationAmountsProp,
  onItemsChange,
  storeId: storeIdProp,
  storeType,
  charityName = '',
  isReadOnly: isReadOnlyProp,
  isItemTitleEditable: isItemTitleEditableProp = false,
  isPricingReadOnly: isPricingReadOnlyProp,
  moqShippingLane = 'standard',
  onShippingLaneRequoteComplete,
}) => {
  const resolvedStoreType: StoreReviewStoreType = storeType || undefined;
  const navigate = useNavigate();
  const { orderId: storeIdFromRoute } = useParams<{ orderId?: string }>();
  const storeId = storeIdProp ?? storeIdFromRoute ?? null;
  const [deleteApiServerStoreDesignGroup] = useDeleteApiServerStoreDesignGroupMutation();
  const [deleteApiServerStoreDesigns] = useDeleteApiServerStoreDesignsMutation();
  const [updateStore] = useUpdateStoreMutation();
  const [updateStoreDonationMarkup] = useUpdateStoreDonationMarkupMutation();
  const productTitleUpdateTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const donationMarkupUpdateTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const storePricingSaveTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const lastSavedStorePricingKeyRef = useRef<Record<number, string>>({});
  const pendingStorePricingKeyRef = useRef<Record<number, string>>({});
  const hasTrackedOrderDetailsSectionRef = useRef(false);
  const rawScreen = useViewport(true);
  const viewportData = useContext(ViewportContext);
  const viewportWidth = viewportData.width;
  // Keep the mobile layout through the 620–767px range where tabSmall breaks.
  const screen = viewportWidth < MOBILE_LAYOUT_MAX_WIDTH ? ViewPort.mobile : rawScreen;
  const styleClass = `store-review-order-step--${screen}`;
  const isMobile = screen === ViewPort.mobile;
  const isTablet =
    screen === ViewPort.tabSmall || screen === ViewPort.tab || screen === ViewPort.tabBig;
  // For 992px to 1244px, use desktop layout (side modal)
  const isDesktopRange = viewportWidth >= 992 && viewportWidth <= 1244;
  const isMobileOrTablet = (isMobile || isTablet) && !isDesktopRange;
  const breakdownScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const breakdownScrollSnapshotRef = useRef<IPageScrollSnapshot | null>(null);

  const restoreBreakdownBackgroundScroll = useCallback(() => {
    const snapshot = breakdownScrollSnapshotRef.current;
    if (!snapshot) {
      return;
    }
    restorePageScrollSnapshot(snapshot);
  }, []);
  const handleBreakdownModalScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { currentTarget } = event;
    currentTarget.classList.add('breakdown-modal-scroll-active');

    if (breakdownScrollTimeoutRef.current !== null) {
      clearTimeout(breakdownScrollTimeoutRef.current);
    }

    breakdownScrollTimeoutRef.current = setTimeout(() => {
      currentTarget.classList.remove('breakdown-modal-scroll-active');
      breakdownScrollTimeoutRef.current = null;
    }, 800);
  }, []);

  useEffect(
    () => () => {
      if (breakdownScrollTimeoutRef.current !== null) {
        clearTimeout(breakdownScrollTimeoutRef.current);
      }
    },
    []
  );

  // Check if we're on edit page and get edit mode
  const isOnEditPage = window.location.pathname.includes('/store/edit/');
  const editModeCtx = useEditModeSafe();

  // Read-only: locked fields on edit (1+ order mode). View-only features (e.g. breakpoint overlay) stay available.
  const isReviewStepReadOnly = isReadOnlyProp ?? editModeCtx?.isFieldsDisabled ?? isOnEditPage;
  const isItemTitleEditable = isItemTitleEditableProp;
  const isPricingReadOnly = isPricingReadOnlyProp ?? isReviewStepReadOnly;
  const shouldAutoSelectPricingDefaults = isOnEditPage || isPricingReadOnly;
  const isPricingInteractionEnabled = !isPricingReadOnly;
  const isItemTitleInputDisabled = isReviewStepReadOnly && !isItemTitleEditable;
  const { user } = useAppSelector((state: RootState) => state.user);
  const uiVariationCtx = useStoreCheckoutUiVariationOptional();
  const designGroupMoqByKey = useAppSelector((state) => state.storeCart.designGroupMoqByKey);
  const designGroupMoqFetchRequestKey = useAppSelector(
    (state) => state.storeCart.designGroupMoqFetchRequestKey
  );
  const isDesignGroupMoqLoading = useAppSelector(
    (state) => state.storeCart.isDesignGroupMoqLoading
  );
  const designGroupApiStatusByKey = useAppSelector(
    (state) => state.storeCart.designGroupApiStatusByKey
  );

  useStoreCartDesignGroupMoqs(items, { shippingLane: moqShippingLane });

  const expectedMoqRequestKey = useMemo(
    () => getStoreCartDesignGroupMoqRequestKey(items, moqShippingLane),
    [items, moqShippingLane]
  );

  const isMoqReadyForCurrentShippingLane = useMemo(
    () =>
      !expectedMoqRequestKey ||
      (!isDesignGroupMoqLoading && designGroupMoqFetchRequestKey === expectedMoqRequestKey),
    [designGroupMoqFetchRequestKey, expectedMoqRequestKey, isDesignGroupMoqLoading]
  );

  const getDesignGroupMoq = useCallback(
    (group: DesignGroup): number | undefined => {
      const groupKey = getStoreReviewDesignGroupKey(group);

      return groupKey ? designGroupMoqByKey[groupKey] : undefined;
    },
    [designGroupMoqByKey]
  );

  const getDesignGroupMoqApiStatus = useCallback(
    (group: DesignGroup) => {
      const groupKey = getStoreReviewDesignGroupKey(group);

      return groupKey
        ? getStoreCartDesignGroupApiStatus(designGroupApiStatusByKey, groupKey)
        : DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;
    },
    [designGroupApiStatusByKey]
  );

  const persistProductTitleUpdate = useCallback(
    (orderItemId: number, productTitle: string) => {
      if (!storeId || isReviewStepReadOnly) {
        return;
      }

      const timers = productTitleUpdateTimersRef.current;
      const existingTimer = timers[orderItemId];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      timers[orderItemId] = setTimeout(() => {
        delete timers[orderItemId];
        updateStore(buildStoreProductTitleUpdatePayload(Number(storeId), orderItemId, productTitle))
          .unwrap()
          .catch(() => {
            /* product title save is best-effort when store exists */
          });
      }, 500);
    },
    [isReviewStepReadOnly, storeId, updateStore]
  );

  const persistDonationMarkupUpdate = useCallback(
    (orderItemId: number, amount: string) => {
      if (!storeId || isPricingReadOnly || !isStoreDonationMarkupStoreType(resolvedStoreType)) {
        return;
      }

      const timers = donationMarkupUpdateTimersRef.current;
      const existingTimer = timers[orderItemId];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      timers[orderItemId] = setTimeout(() => {
        delete timers[orderItemId];
        updateStoreDonationMarkup(
          buildStoreDonationMarkupUpdatePayload(
            Number(storeId),
            orderItemId,
            amount,
            resolvedStoreType
          )
        )
          .unwrap()
          .catch(() => {
            /* donation/markup save is best-effort when store exists */
          });
      }, STORE_DONATION_MARKUP_UPDATE_DEBOUNCE_MS);
    },
    [isPricingReadOnly, resolvedStoreType, storeId, updateStoreDonationMarkup]
  );

  useEffect(
    () => () => {
      Object.values(donationMarkupUpdateTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
    },
    []
  );

  useEffect(
    () => () => {
      Object.values(productTitleUpdateTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
    },
    []
  );

  const showAiImageGenerationActions =
    !uiVariationCtx || showsStoreCheckoutAiImageGenerationActions(uiVariationCtx.uiVariation);
  const isSingleImageGenerationVariation = uiVariationCtx
    ? isSingleImageGenerationStoreCheckoutUiVariation(uiVariationCtx.uiVariation)
    : false;
  const isErrorStateVariation = uiVariationCtx
    ? isErrorStateStoreCheckoutUiVariation(uiVariationCtx.uiVariation)
    : false;

  const [selectedPricingModels, setSelectedPricingModels] = useState<{
    [key: number]: 'drops' | 'same' | null;
  }>({});
  const [selectedMinimums, setSelectedMinimums] = useState<{ [key: number]: number }>({});

  const buildDesignGroupsWithPricing = useCallback(
    (
      groups: DesignGroup[],
      pricingModels: { [key: number]: 'drops' | 'same' | null }
    ): DesignGroup[] =>
      groups.map((group, index) => ({
        ...group,
        pricingModel: pricingModels[index] ?? null,
      })),
    []
  );

  const maybeTrackOrderDetailsSectionCompleted = useCallback(
    (groupsWithPricing: DesignGroup[]) => {
      if (hasTrackedOrderDetailsSectionRef.current || isReviewStepReadOnly || isOnEditPage) {
        return;
      }

      const hasOrderItems = groupsWithPricing.some((group) => group.items.length > 0);
      if (!hasOrderItems) {
        return;
      }

      if (
        !hasAllStoreReviewProductTitles(groupsWithPricing) ||
        !hasAllDesignGroupsPricingSelected(groupsWithPricing, resolvedStoreType ?? '')
      ) {
        return;
      }

      hasTrackedOrderDetailsSectionRef.current = true;
      const email = user?.email ?? '';
      captureStoresFormSectionCompleted(email, STORES_FORM_MIXPANEL_SECTIONS.ORDER_DETAILS);
      captureStoresFormSectionCompleted(email, STORES_FORM_MIXPANEL_SECTIONS.SIZES);
    },
    [isOnEditPage, isReviewStepReadOnly, resolvedStoreType, user?.email]
  );

  const [showBreakdownModal, setShowBreakdownModal] = useState<{
    isOpen: boolean;
    groupIndex: number | null;
  }>({ isOpen: false, groupIndex: null });

  const openBreakdownModal = useCallback((groupIndex: number) => {
    breakdownScrollSnapshotRef.current = capturePageScrollSnapshot();
    setShowBreakdownModal({ isOpen: true, groupIndex });
  }, []);

  const closeBreakdownModal = useCallback(() => {
    const snapshot = breakdownScrollSnapshotRef.current;
    setShowBreakdownModal({ isOpen: false, groupIndex: null });
    if (snapshot) {
      requestAnimationFrame(() => {
        restorePageScrollSnapshot(snapshot);
        requestAnimationFrame(() => restorePageScrollSnapshot(snapshot));
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!showBreakdownModal.isOpen) {
      return;
    }
    restoreBreakdownBackgroundScroll();
    requestAnimationFrame(restoreBreakdownBackgroundScroll);
  }, [showBreakdownModal.isOpen, restoreBreakdownBackgroundScroll]);

  // State for selected images per product
  const [selectedImages, setSelectedImages] = useState<{ [key: string]: string }>({});

  const [progressiveModalOpen, setProgressiveModalOpen] = useState(false);
  const [progressiveModalTargetItemId, setProgressiveModalTargetItemId] = useState<number | null>(
    null
  );
  const [progressiveModalSource, setProgressiveModalSource] = useState<StoreAiPreviewVibe | null>(
    null
  );

  // State for donation/markup amount per product (controlled from StoreCheckout when provided)
  const [internalDonationAmounts, setInternalDonationAmounts] = useState<{ [key: string]: string }>(
    () => getInitialStoreReviewDonationAmounts(donationAmountsProp)
  );
  const donationAmounts = donationAmountsProp ?? internalDonationAmounts;

  const setDonationAmounts = useCallback(
    (nextAmounts: Record<string, string>) => {
      if (donationAmountsProp === undefined) {
        setInternalDonationAmounts(nextAmounts);
      }
      onDonationAmountsChange?.(nextAmounts);
    },
    [donationAmountsProp, onDonationAmountsChange]
  );

  const handleDonationAmountChange = useCallback(
    (donationKey: string, orderItemId: number, amount: string) => {
      const sanitizedAmount = sanitizeStoreDonationMarkupInput(amount);

      setDonationAmounts({
        ...donationAmounts,
        [donationKey]: sanitizedAmount,
      });
      persistDonationMarkupUpdate(orderItemId, sanitizedAmount);
    },
    [donationAmounts, persistDonationMarkupUpdate, setDonationAmounts]
  );

  const handleDonationAmountBlur = useCallback(
    (donationKey: string, orderItemId: number, amount: string) => {
      const normalizedAmount = normalizeStoreDonationMarkupOnBlur(amount);

      if (normalizedAmount === amount) {
        return;
      }

      setDonationAmounts({
        ...donationAmounts,
        [donationKey]: normalizedAmount,
      });
      persistDonationMarkupUpdate(orderItemId, normalizedAmount);
    },
    [donationAmounts, persistDonationMarkupUpdate, setDonationAmounts]
  );

  // Group items by design group (same logic as StoreCart)
  const groupedDesignGroups = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }

    // Group items by designGroupItemId, proofItemId, or proofId
    const groupedItems = items.reduce((acc, item) => {
      const groupKey = item.designGroupItemId ?? item.proofItemId ?? item.proofId;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {} as Record<string | number, ICartItemDetail[]>);

    // Convert grouped items to DesignGroup array with proper names (Design A, Design B, etc.)
    return Object.entries(groupedItems).map(([, groupItems], index) => ({
      name: getDesignGroupLabel(index),
      items: groupItems,
      designGroupId: groupItems[0]?.designGroupItemId ?? null,
    }));
  }, [items]);

  // State for managing design groups
  const [designGroups, setDesignGroups] = useState<DesignGroup[]>(groupedDesignGroups);

  // Update design groups when items change
  useEffect(() => {
    setDesignGroups((prevGroups) => {
      if (
        prevGroups.length === groupedDesignGroups.length &&
        prevGroups.every((group, groupIndex) => {
          const incomingGroup = groupedDesignGroups[groupIndex];

          return (
            group.items.length === incomingGroup.items.length &&
            group.items.every((item, itemIndex) => item.id === incomingGroup.items[itemIndex].id)
          );
        })
      ) {
        return prevGroups.map((group, groupIndex) => {
          const incomingGroup = groupedDesignGroups[groupIndex];

          return {
            ...incomingGroup,
            items: incomingGroup.items.map((incomingItem, itemIndex) => {
              const previousItem = group.items[itemIndex];
              const previousPriceListLength = previousItem.priceList?.length ?? 0;
              const incomingPriceListLength = incomingItem.priceList?.length ?? 0;

              if (previousPriceListLength > incomingPriceListLength) {
                return {
                  ...incomingItem,
                  price: previousItem.price ?? incomingItem.price,
                  priceList: previousItem.priceList ?? incomingItem.priceList,
                  minQuant: previousItem.minQuant ?? incomingItem.minQuant,
                  baseUnitPrice: previousItem.baseUnitPrice ?? incomingItem.baseUnitPrice,
                  strikedPricePerUnit:
                    previousItem.strikedPricePerUnit ?? incomingItem.strikedPricePerUnit,
                };
              }

              return incomingItem;
            }),
          };
        });
      }

      return groupedDesignGroups;
    });
  }, [groupedDesignGroups]);

  useEffect(() => {
    setSelectedPricingModels((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      designGroups.forEach((group, index) => {
        if (next[index] != null) {
          return;
        }

        const savedPricingModel = resolveStoreReviewPricingModelFromGroup(group);

        if (savedPricingModel) {
          next[index] = savedPricingModel;
          hasChanges = true;
          return;
        }

        if (!shouldAutoSelectPricingDefaults) {
          return;
        }

        next[index] = inferStoreReviewPricingModelFromGroup(group);
        hasChanges = true;
      });

      return hasChanges ? next : prev;
    });
  }, [designGroups, shouldAutoSelectPricingDefaults]);

  // Notify parent when design groups or pricing models change
  useEffect(() => {
    if (onDesignGroupsChange) {
      const groupsWithPricing = designGroups.map((group, index) => ({
        ...group,
        pricingModel: selectedPricingModels[index] || null,
      }));
      onDesignGroupsChange(groupsWithPricing);
    }
  }, [designGroups, selectedPricingModels, onDesignGroupsChange]);

  const saveGroupPricingForGroup = useCallback(
    (groupIndex: number, minimumQuantity?: number) => {
      if (!storeId || isPricingReadOnly) {
        return;
      }

      const group = designGroups[groupIndex];
      const pricingModel = selectedPricingModels[groupIndex];

      if (!group || (pricingModel !== 'drops' && pricingModel !== 'same')) {
        return;
      }

      const designGroupMoq = getDesignGroupMoq(group);
      const designGroupMoqApiStatus = getDesignGroupMoqApiStatus(group);
      const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(designGroupMoqApiStatus);
      const { quantities } = getStoreReviewPricingBreakpoints(
        group,
        hasMoqTiers ? designGroupMoq : undefined,
        { savedBreakpointsOnly: isOnEditPage }
      );
      const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
      const resolvedMinimum = minimumQuantity ?? selectedMinimums[groupIndex] ?? defaultMinimum;

      if (!Number.isFinite(resolvedMinimum) || resolvedMinimum <= 0) {
        return;
      }

      // Wait for group-level quotes so breakpoints are not saved empty/wrong.
      if (!areStoreReviewItemsQuotedAtQuantity(group.items, resolvedMinimum)) {
        return;
      }

      const saveKey = buildStorePricingSaveKey(group, resolvedMinimum, pricingModel);

      if (
        lastSavedStorePricingKeyRef.current[groupIndex] === saveKey ||
        pendingStorePricingKeyRef.current[groupIndex] === saveKey
      ) {
        return;
      }

      const existingTimer = storePricingSaveTimersRef.current[groupIndex];

      if (existingTimer) {
        clearTimeout(existingTimer);
        delete pendingStorePricingKeyRef.current[groupIndex];
      }

      pendingStorePricingKeyRef.current[groupIndex] = saveKey;

      storePricingSaveTimersRef.current[groupIndex] = setTimeout(() => {
        delete storePricingSaveTimersRef.current[groupIndex];
        delete pendingStorePricingKeyRef.current[groupIndex];
        lastSavedStorePricingKeyRef.current[groupIndex] = saveKey;

        // #region agent log
        fetch('http://127.0.0.1:7533/ingest/039f2598-1ae5-494b-9857-3e928ecbad58', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Debug-Session-Id': 'bbce4b',
          },
          body: JSON.stringify({
            sessionId: 'bbce4b',
            runId: 'pre-fix',
            hypothesisId: 'H-D',
            location: 'StoreReviewOrderStep.tsx:saveGroupPricingForGroup',
            message: 'Auto-saving group pricing',
            data: {
              groupIndex,
              pricingModel,
              resolvedMinimum,
              itemPriceListSummaries: group.items.map((item) => ({
                id: item.id,
                minQuant: item.minQuant,
                price: item.price,
                priceListLength: item.priceList?.length ?? 0,
                priceList: (item.priceList ?? []).map((bp) => ({
                  quantity: Number(bp.quantity),
                  finalPricePerUnit: bp.finalPricePerUnit,
                })),
              })),
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion

        updateStore(
          buildStorePricingUpdatePayload({
            orderId: Number(storeId),
            group,
            minimumQuantity: resolvedMinimum,
            pricingModel,
            designGroupMoq: hasMoqTiers ? designGroupMoq : undefined,
            donationAmounts,
            groupIndex,
            storeType: resolvedStoreType ?? '',
          })
        )
          .unwrap()
          .catch(() => {
            delete lastSavedStorePricingKeyRef.current[groupIndex];
          });
      }, 300);
    },
    [
      designGroups,
      donationAmounts,
      getDesignGroupMoq,
      getDesignGroupMoqApiStatus,
      isOnEditPage,
      isPricingReadOnly,
      resolvedStoreType,
      selectedMinimums,
      selectedPricingModels,
      storeId,
      updateStore,
    ]
  );

  const { markUserSelectedGroupMinimum, clearUserSelectedGroupMinimum } =
    useStoreReviewDesignGroupQuotes({
      designGroups,
      selectedMinimums,
      allItems: items,
      isPricingReadOnly,
      moqShippingLane,
      designGroupMoqFetchRequestKey,
      isDesignGroupMoqLoading,
      getDesignGroupMoq,
      getDesignGroupMoqApiStatus,
      onQuotedItemsChange: onItemsChange ?? (() => undefined),
      setDesignGroups,
      onShippingLaneRequoteComplete,
    });

  const handlePricingModelSelect = useCallback(
    (groupIndex: number, pricingModel: StoreReviewPricingModel) => {
      if (!isPricingInteractionEnabled) {
        return;
      }

      if (selectedPricingModels[groupIndex] === pricingModel) {
        return;
      }

      const group = designGroups[groupIndex];

      if (group) {
        const designGroupMoq = getDesignGroupMoq(group);
        const designGroupMoqApiStatus = getDesignGroupMoqApiStatus(group);
        const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(designGroupMoqApiStatus);
        const { quantities } = getStoreReviewPricingBreakpoints(
          group,
          hasMoqTiers ? designGroupMoq : undefined,
          { savedBreakpointsOnly: isOnEditPage }
        );
        const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
        const resolvedMinimum = selectedMinimums[groupIndex] ?? defaultMinimum;

        // Default min is shown in the UI; treat it like an explicit selection so
        // group-level quotes (and correct breakpoint prices) run immediately.
        if (Number.isFinite(resolvedMinimum) && resolvedMinimum > 0) {
          markUserSelectedGroupMinimum(groupIndex, resolvedMinimum);
          setSelectedMinimums((prev) => ({
            ...prev,
            [groupIndex]: resolvedMinimum,
          }));
        }
      }

      setSelectedPricingModels((prev) => {
        const next = {
          ...prev,
          [groupIndex]: pricingModel,
        };

        maybeTrackOrderDetailsSectionCompleted(buildDesignGroupsWithPricing(designGroups, next));

        return next;
      });
    },
    [
      buildDesignGroupsWithPricing,
      designGroups,
      getDesignGroupMoq,
      getDesignGroupMoqApiStatus,
      isOnEditPage,
      isPricingInteractionEnabled,
      markUserSelectedGroupMinimum,
      maybeTrackOrderDetailsSectionCompleted,
      selectedMinimums,
      selectedPricingModels,
    ]
  );

  const { breakdownPricingByItemId, isBreakdownPricingLoading } = useStoreReviewBreakdownPricing({
    showBreakdownModal,
    designGroups,
    selectedMinimums,
    allItems: items,
    isPricingReadOnly,
    moqShippingLane,
    getDesignGroupMoq,
    getDesignGroupMoqApiStatus,
  });

  useEffect(() => {
    if (!storeId || isPricingReadOnly) {
      return;
    }

    designGroups.forEach((_group, groupIndex) => {
      const pricingModel = selectedPricingModels[groupIndex];

      if (pricingModel !== 'drops' && pricingModel !== 'same') {
        return;
      }

      saveGroupPricingForGroup(groupIndex);
    });
  }, [
    designGroups,
    isPricingReadOnly,
    saveGroupPricingForGroup,
    selectedMinimums,
    selectedPricingModels,
    storeId,
  ]);

  useEffect(
    () => () => {
      Object.values(storePricingSaveTimersRef.current).forEach((timer) => {
        clearTimeout(timer);
      });
    },
    []
  );

  useEffect(() => {
    if (!isMoqReadyForCurrentShippingLane) {
      return;
    }

    setSelectedMinimums((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      designGroups.forEach((group, index) => {
        const apiStatus = getDesignGroupMoqApiStatus(group);
        const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(apiStatus);

        if (!hasMoqTiers && !shouldAutoSelectPricingDefaults) {
          return;
        }

        const designGroupMoq = hasMoqTiers ? getDesignGroupMoq(group) : undefined;
        const { quantities } = getStoreReviewPricingBreakpoints(group, designGroupMoq, {
          savedBreakpointsOnly: isOnEditPage,
        });
        const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
        const currentMinimum = next[index];

        if (currentMinimum == null) {
          next[index] = defaultMinimum;
          hasChanges = true;
          return;
        }

        const resolvedMinimum = resolveStoreReviewGroupMinimumQuantity(
          group,
          quantities,
          currentMinimum
        );

        if (resolvedMinimum !== currentMinimum) {
          next[index] = resolvedMinimum;

          if (resolvedMinimum === defaultMinimum) {
            clearUserSelectedGroupMinimum(index);
          }

          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [
    clearUserSelectedGroupMinimum,
    designGroups,
    getDesignGroupMoq,
    getDesignGroupMoqApiStatus,
    isMoqReadyForCurrentShippingLane,
    isOnEditPage,
    shouldAutoSelectPricingDefaults,
  ]);

  useEffect(() => {
    if (donationAmountsProp !== undefined) {
      return;
    }
    onDonationAmountsChange?.(donationAmounts);
  }, [donationAmounts, donationAmountsProp, onDonationAmountsChange]);

  // Initialize selected images for each product with first thumbnail or main image
  useEffect(() => {
    const initialSelections: { [key: string]: string } = {};
    designGroups.forEach((group, groupIndex) => {
      group.items?.forEach((item, itemIndex) => {
        const productKey = `${groupIndex}-${itemIndex}`;
        if (!selectedImages[productKey]) {
          const thumbnails = [
            item.primaryImage || item.frontImage,
            item.backImage,
            item.designImgPrimary,
          ].filter(Boolean) as string[];

          // Main image - prioritize uploaded main image if available
          const mainImage = item.primaryImage || item.frontImage || '';
          const defaultImage = thumbnails[0] || mainImage || '';
          if (defaultImage) {
            initialSelections[productKey] = defaultImage;
          }
        }
      });
    });
    if (Object.keys(initialSelections).length > 0) {
      setSelectedImages((prev) => ({ ...prev, ...initialSelections }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designGroups]);

  const redirectIfCartEmpty = useCallback(
    (remainingItems: ICartItemDetail[]) => {
      if (remainingItems.length === 0) {
        navigate(STORE_NAVIGATION.STORE_CART);
      }
    },
    [navigate]
  );

  const applyItemsRemoval = useCallback(
    (removedItemIds: number[], removedGroupIndex?: number) => {
      const remainingItems = getRemainingItemsAfterRemoval(items, removedItemIds);
      onItemsChange?.(remainingItems);

      if (removedGroupIndex != null) {
        setSelectedPricingModels((prev) => reindexRecordAfterGroupRemoval(prev, removedGroupIndex));
        setSelectedMinimums((prev) => reindexRecordAfterGroupRemoval(prev, removedGroupIndex));
      }

      setDesignGroups((prevGroups) => {
        const nextGroups = reorderDesignGroupsAfterRemoval(
          prevGroups
            .map((group, groupIndex) =>
              removedGroupIndex === groupIndex
                ? null
                : {
                    ...group,
                    items: group.items.filter((item) => !removedItemIds.includes(item.id)),
                  }
            )
            .filter((group): group is DesignGroup => Boolean(group && group.items.length > 0))
        );
        return nextGroups;
      });

      redirectIfCartEmpty(remainingItems);
    },
    [items, onItemsChange, redirectIfCartEmpty]
  );

  const handleItemTitleChange = useCallback(
    (groupIndex: number, itemIndex: number, value: string) => {
      if (isItemTitleInputDisabled) {
        return;
      }

      let orderItemId: number | null = null;

      setDesignGroups((prevGroups) => {
        const targetItem = prevGroups[groupIndex]?.items[itemIndex];
        orderItemId = targetItem?.id ?? null;

        const nextGroups = prevGroups.map((group, gIdx) =>
          gIdx !== groupIndex
            ? group
            : {
                ...group,
                items: group.items.map((groupItem, iIdx) =>
                  iIdx !== itemIndex ? groupItem : { ...groupItem, productTitle: value }
                ),
              }
        );
        onItemsChange?.(nextGroups.flatMap((group) => group.items));
        maybeTrackOrderDetailsSectionCompleted(
          buildDesignGroupsWithPricing(nextGroups, selectedPricingModels)
        );
        return nextGroups;
      });

      if (orderItemId) {
        persistProductTitleUpdate(orderItemId, value);
      }
    },
    [
      buildDesignGroupsWithPricing,
      isItemTitleInputDisabled,
      maybeTrackOrderDetailsSectionCompleted,
      onItemsChange,
      persistProductTitleUpdate,
      selectedPricingModels,
    ]
  );

  const handleRemoveDesignGroup = useCallback(
    async (groupIndex: number, event?: React.MouseEvent) => {
      event?.stopPropagation();
      if (isReviewStepReadOnly) {
        return;
      }

      const group = designGroups[groupIndex];
      if (!group) {
        return;
      }

      const removedItemIds = group.items.map((item) => item.id);
      const designGroupId = getDesignGroupIdFromGroup(group);

      try {
        if (storeId && designGroupId != null) {
          await deleteApiServerStoreDesignGroup({
            storeId,
            designGroupId,
          }).unwrap();
        } else if (storeId) {
          const designIds = [
            ...new Set(group.items.map((item) => item.designId).filter((designId) => designId > 0)),
          ];
          if (designIds.length > 0) {
            await deleteApiServerStoreDesigns({ storeId, designIds }).unwrap();
          }
        }

        applyItemsRemoval(removedItemIds, groupIndex);
        showToastrMessage(STORE_MESSAGES.DESIGN_GROUP_REMOVED, 'success');
      } catch {
        showToastrMessage(STORE_MESSAGES.DESIGN_GROUP_REMOVE_FAILED, 'failure');
      }
    },
    [
      applyItemsRemoval,
      deleteApiServerStoreDesignGroup,
      deleteApiServerStoreDesigns,
      designGroups,
      isReviewStepReadOnly,
      storeId,
    ]
  );

  const handleRemoveItem = useCallback(
    async (groupIndex: number, itemIndex: number, event?: React.MouseEvent) => {
      event?.stopPropagation();
      if (isReviewStepReadOnly) {
        return;
      }

      const group = designGroups[groupIndex];
      const item = group?.items[itemIndex];
      if (!item) {
        return;
      }

      const relatedItems = group.items.filter(
        (groupItem) => groupItem.proofItemId === item.proofItemId
      );
      const removedItemIds = relatedItems.map((groupItem) => groupItem.id);
      const designIds = [
        ...new Set(
          relatedItems.map((groupItem) => groupItem.designId).filter((designId) => designId > 0)
        ),
      ];

      try {
        if (storeId && designIds.length > 0) {
          await deleteApiServerStoreDesigns({ storeId, designIds }).unwrap();
        }

        const willRemoveEntireGroup = group.items.length <= relatedItems.length;
        applyItemsRemoval(removedItemIds, willRemoveEntireGroup ? groupIndex : undefined);
        showToastrMessage(STORE_MESSAGES.ORDER_ITEM_REMOVED, 'success');
      } catch {
        showToastrMessage(STORE_MESSAGES.ORDER_ITEM_REMOVE_FAILED, 'failure');
      }
    },
    [applyItemsRemoval, deleteApiServerStoreDesigns, designGroups, isReviewStepReadOnly, storeId]
  );

  if (!items || items.length === 0) {
    return null;
  }

  const openProgressiveStyleModal = (
    orderItemId: number,
    presetStyleId: StoreAiPreviewVibe
  ): void => {
    if (isReviewStepReadOnly) {
      return;
    }
    setProgressiveModalTargetItemId(orderItemId);
    setProgressiveModalSource(presetStyleId);
    setProgressiveModalOpen(true);
  };

  const closeProgressiveStyleModal = (): void => {
    setProgressiveModalOpen(false);
    setProgressiveModalTargetItemId(null);
    setProgressiveModalSource(null);
  };

  const onProgressiveImagesCommitted = (slots: IProgressiveStyleSlots): void => {
    if (progressiveModalTargetItemId == null) {
      return;
    }
    let productKeyForSelection: string | null = null;
    designGroups.forEach((group, gIdx) => {
      group.items.forEach((groupItem, iIdx) => {
        if (groupItem.id === progressiveModalTargetItemId) {
          productKeyForSelection = `${gIdx}-${iIdx}`;
        }
      });
    });
    setDesignGroups((prevGroups) =>
      applyProgressiveStyleSlotsToDesignGroups(
        prevGroups,
        progressiveModalTargetItemId,
        slots,
        progressiveModalSource,
        isSingleImageGenerationVariation
      )
    );
    if (productKeyForSelection != null) {
      const selectionKey = productKeyForSelection;
      setSelectedImages((prev) => ({ ...prev, [selectionKey]: slots.front }));
    }
    closeProgressiveStyleModal();
  };

  // Render individual product card
  const renderProductCard = (item: ICartItemDetail, index: number, groupIndex: number) => {
    const productKey = `${groupIndex}-${index}`;
    const donationKey = `donation-${productKey}`;
    const donationAmount = donationAmounts[donationKey] || '';
    const userOrg = user?.accountType !== 'corporate' ? user?.organization ?? '' : '';
    const designRejectionInfo =
      item.designRejectionInfo ??
      isDesignRejected(item.fromDesignTool, item.designLicensing, item.designPricing, userOrg);
    const { isDesignLicensingRejected, isDesignSelfBlockedForCheckout, checkoutComment, comment } =
      designRejectionInfo;
    const designRejectionMessage = checkoutComment || comment;
    const showDesignRejectionSection =
      (isDesignLicensingRejected || isDesignSelfBlockedForCheckout) &&
      Boolean(designRejectionMessage);
    const itemPrice = getStoreReviewItemQuotedPrice(item) ?? 0;
    const showFinalPrice =
      !isPricingReadOnly &&
      (storeType === 'fundraiser' || storeType === 'brand-drop') &&
      shouldShowItemFinalPrice(donationAmount);
    const finalPrice = getItemFinalPriceWithDonation(donationAmount, itemPrice);

    // Get print types and decor methods
    const designPrintTypes = getPrintTypeList(
      item.printTypeInfo ? (item.printTypeInfo as IPrintTypeItem[]) : item.printTypes || []
    );

    const designDecoratingTypes = getDecoratingTypeList(
      item.decorMethodInfo && item.decorMethodInfo.length
        ? (item.decorMethodInfo as IDecoratingItem[])
        : item.decoratingMethod || []
    );

    /**
     * Patterns and/or Colorblock is priced per garment part, so it carries no decorating location
     * and never reaches `designDecoratingTypes` — it shows as its own pills instead.
     */
    const patternColorblockPills = buildPatternColorblockPills(item.decorMethodInfo);

    const designImages = buildDesignImagesFromItem(item);
    const mainImage = item.primaryImage || item.frontImage || designImages[0]?.src || '';
    const selectedFromDesignImages = designImages.find((img) => img.selected)?.src;
    const selectedImage = selectedFromDesignImages || selectedImages[productKey] || mainImage;

    // Color options - ensure at least one color exists
    const colorOptions: IDesignGroupColorOption[] = [
      {
        designId: index + 1,
        colorName: item.apparelColor || 'Blue Jeans',
        colorHex: item.apparelHex || '#4A6B8A',
        hexCode: item.apparelHex || '#4A6B8A',
        primaryImage: item.primaryImage || item.frontImage || '',
        secondaryImage: item.designImgSecondary || item.backImage || '',
        designImage: item.designImgPrimary || '',
        proofItemId: item.proofItemId || 0,
        apparelColor: item.apparelColor || 'Blue Jeans',
        designPricing: item.designPricing,
        designLicensing: item.designLicensing,
        colorCode: item.apparelHex,
        apparel: {
          styleCode: item.apparelStyleCode,
          name: item.apparelName,
          brand: item.apparelBrand,
          color: item.apparelColor,
          hexCode: item.apparelHex,
        },
      },
    ].filter((color) => color.hexCode && color.colorName);

    const productInfoBullets = parseStoreReviewProductInfoBullets(item.productDescription);

    const handleImageSelect = (imageSrc: string, imageIndex: number) => {
      setSelectedImages((prev) => ({
        ...prev,
        [productKey]: imageSrc,
      }));
      setDesignGroups((prevGroups) =>
        prevGroups.map((group, gIdx) =>
          gIdx !== groupIndex
            ? group
            : {
                ...group,
                items: group.items.map((groupItem, iIdx) =>
                  iIdx !== index
                    ? groupItem
                    : {
                        ...groupItem,
                        designImages: buildDesignImagesFromItem(groupItem).map((img, idx) => ({
                          ...img,
                          selected: idx === imageIndex,
                        })),
                      }
                ),
              }
        )
      );
    };

    return (
      <div className={`${styleClass}__product-card`}>
        {!isReviewStepReadOnly && (
          <div className={`${styleClass}__order-item-header`}>
            <div className={`${styleClass}__order-item-number`}>
              Order Item #
              {item.alphaNumericProofItemId ||
                `${groupIndex + 1}${String.fromCharCode(65 + index)}`}
            </div>
            <ToolTip title="Remove Order Item" placement="top">
              <button
                type="button"
                className={`${styleClass}__order-item-actions`}
                onClick={(event) => handleRemoveItem(groupIndex, index, event)}
                aria-label="Remove order item"
              >
                <Icon source="/src/assets/svg/remove.svg" width="24px" height="24px" />
              </button>
            </ToolTip>
          </div>
        )}

        {showDesignRejectionSection && (
          <div className={`${styleClass}__design-rejection-section`}>{designRejectionMessage}</div>
        )}

        {/* Main Content — greyed/disabled when blocked for self-checkout */}
        <div
          className={`${styleClass}__main-content${
            showDesignRejectionSection ? ` ${styleClass}__main-content--blocked` : ''
          }`}
        >
          {/* Left: Images + AI generation actions */}
          <div className={`${styleClass}__images-section order-item-gallery-column`}>
            <StoresDesignImagesList
              designImages={designImages}
              selectedImageSrc={selectedImage}
              onSelectImage={handleImageSelect}
              previewImagesFromAi={item.previewImagesFromAi}
            />
            {!isReviewStepReadOnly && showAiImageGenerationActions && (
              <StoreReviewItemGalleryFooter
                groupIndex={groupIndex}
                itemIndex={index}
                onCreateFlatLays={() =>
                  openProgressiveStyleModal(getReviewOrderItemId(item), 'ai-flat-lay')
                }
                onCreateModelImages={() =>
                  openProgressiveStyleModal(getReviewOrderItemId(item), 'ai-model')
                }
              />
            )}
          </div>

          {/* Right: Product Details */}
          <div className={`${styleClass}__details-section`}>
            {/* Product Title and Attributes */}
            <div className={`${styleClass}__product-header`}>
              <div className={`${styleClass}__product-header-content`}>
                <div className={`${styleClass}__product-title-input`}>
                  <Text
                    as="label"
                    className={`${styleClass}__product-title-label ${styleClass}__label`}
                    color="#8D90AA"
                    size={12}
                    weight="bold"
                  >
                    {STORE_REVIEW_PRODUCT_TITLE_LABEL}
                  </Text>
                  <Input
                    id={`itemTitle-${productKey}`}
                    name={`itemTitle-${productKey}`}
                    label=""
                    aria-label={STORE_REVIEW_PRODUCT_TITLE_LABEL}
                    value={getStoreReviewProductTitleInputValue(item)}
                    onChange={(_e, data) => {
                      handleItemTitleChange(groupIndex, index, data.value);
                    }}
                    containerClass={`${styleClass}__product-title-input-container`}
                    variant="large"
                    disabled={isItemTitleInputDisabled}
                  />
                </div>

                <div className={`${styleClass}__product-attributes`}>
                  <Text as="span">{item.apparelBrand}</Text>
                  <span className={`${styleClass}__product-attributes__separator`}>•</span>
                  <Text as="span">#{item.apparelStyleCode}</Text>
                </div>

                {(designPrintTypes.length > 0 || designDecoratingTypes.length > 0) && (
                  <div className={`${styleClass}__print-decor-info`}>
                    <PrintDecorComponent
                      printTypeList={designPrintTypes}
                      decorMethodList={designDecoratingTypes}
                      isDefaultPrintType={item.isDefaultPrintType}
                      isDefaultDecoratingInfo={item.isDefaultDecoratingInfo}
                    />
                  </div>
                )}

                <PatternColorblockPills
                  className={`${styleClass}__pattern-colorblock`}
                  pills={patternColorblockPills}
                />
              </div>
            </div>

            {/* Product Info Container */}
            <div className={`${styleClass}__product-info-container`}>
              <div className={`${styleClass}__product-material-info`}>
                <Text
                  as="label"
                  className={`${styleClass}__label`}
                  color="#8D90AA"
                  size={12}
                  weight="bold"
                >
                  {STORE_REVIEW_PRODUCT_INFO_LABEL}
                </Text>
                {productInfoBullets.length > 0 ? (
                  <ul className={`${styleClass}__product-material-info__list`}>
                    {productInfoBullets.map((bullet, bulletIndex) => (
                      <li
                        key={`${productKey}-product-info-${bulletIndex}`}
                        className={`${styleClass}__product-material-info__item`}
                      >
                        {bullet}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Text
                    as="p"
                    className={`${styleClass}__product-material-info__empty`}
                    color="#474B6D"
                    size={14}
                    weight="regular"
                  >
                    {STORE_REVIEW_PRODUCT_INFO_EMPTY_MESSAGE}
                  </Text>
                )}
              </div>

              {/* Color Options */}
              <div className={`${styleClass}__color-options`}>
                <Text
                  as="label"
                  className={`${styleClass}__label`}
                  color="#8D90AA"
                  size={12}
                  weight="bold"
                >
                  {colorOptions.length > 1 ? 'Color Options' : 'Color'}
                </Text>
                <div className={`${styleClass}__color-swatches`}>
                  {colorOptions && colorOptions.length > 0 && (
                    <DesignColorOptions
                      colors={colorOptions}
                      title=""
                      onColorOptionClick={() => {}}
                      size="small"
                      isInactive={isReviewStepReadOnly}
                    />
                  )}
                </div>
              </div>

              {showFinalPrice && (
                <div className={`${styleClass}__price-row`}>
                  <div className={`${styleClass}__price`}>
                    <Text
                      as="label"
                      className={`${styleClass}__label`}
                      color="#8D90AA"
                      size={12}
                      weight="bold"
                    >
                      {STORE_REVIEW_FINAL_PRICE_LABEL}
                    </Text>
                    <div className={`${styleClass}__price-text`}>
                      <span className={`${styleClass}__price-amount`}>
                        {currencyFormat(finalPrice)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Version B: Simplified pricing layout
  const renderVersionB = (group: DesignGroup, groupIndex: number) => {
    const designGroupMoq = getDesignGroupMoq(group);
    const designGroupMoqApiStatus = getDesignGroupMoqApiStatus(group);
    const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(designGroupMoqApiStatus);
    const isMoqPending = isStoreCartDesignGroupMoqPending(designGroupMoqApiStatus);
    const { quantities } = getStoreReviewPricingBreakpoints(
      group,
      hasMoqTiers ? designGroupMoq : undefined,
      { savedBreakpointsOnly: isOnEditPage }
    );
    const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
    const selectedMinimumForPills = selectedMinimums[groupIndex];
    const selectedMinimum = selectedMinimumForPills ?? defaultMinimum;
    const selectedPricingModel = selectedPricingModels[groupIndex] ?? null;
    const getCalculatedItemPrice = (item: ICartItemDetail) =>
      isOnEditPage
        ? getStoreReviewBreakpointUnitPrice(selectedMinimum, item)
        : getStoreReviewItemQuotedPrice(item);
    const productPriceGroups = groupStoreReviewItemsByProduct(
      group.items,
      (item) => getCalculatedItemPrice(item) ?? 0
    );

    return (
      <div
        key={groupIndex}
        className={`${styleClass}__version-b-container order-item-card-pricing`}
      >
        <div className="store-pricing-section">
          <div className="store-pricing-section__design-groups">
            <div className="store-pricing-section__design-group">
              <div className="store-pricing-section__design-group-item-breakpoints">
                {/* Pricing Model Selection at Top - Hide for permanent stores */}
                {storeType !== 'permanent-store' && (
                  <div className={`${styleClass}__pricing-models store-pricing-options`}>
                    {/* Price Drops Card */}
                    <label
                      className={`${styleClass}__pricing-model-card store-pricing-option price-drops ${
                        selectedPricingModel === 'drops'
                          ? `${styleClass}__pricing-model-card--selected-purple`
                          : ''
                      } ${
                        isPricingReadOnly
                          ? `${styleClass}__pricing-model-card--disabled${
                              selectedPricingModel === 'drops'
                                ? ` ${styleClass}__pricing-model-card--selected-disabled`
                                : ` ${styleClass}__pricing-model-card--readonly`
                            }`
                          : ''
                      }`}
                      onClick={() =>
                        !isPricingReadOnly && handlePricingModelSelect(groupIndex, 'drops')
                      }
                    >
                      <div
                        className={`${styleClass}__pricing-model-radio-container pricing-radio-button`}
                      >
                        <input
                          type="radio"
                          name={`pricingModel-${groupIndex}`}
                          checked={selectedPricingModel === 'drops'}
                          onChange={() => handlePricingModelSelect(groupIndex, 'drops')}
                          id={`pricing-drops-${groupIndex}`}
                          className={`${styleClass}__pricing-model-radio`}
                          disabled={isPricingReadOnly}
                        />
                        <label
                          htmlFor={`pricing-drops-${groupIndex}`}
                          className={`${styleClass}__pricing-model-radio-label`}
                        />
                      </div>
                      <div
                        className={`${styleClass}__store-pricing-option-content store-pricing-option-content`}
                      >
                        <div className={`${styleClass}__pricing-model-header option-header`}>
                          <Text
                            as="h3"
                            className={`${styleClass}__pricing-model-title option-title`}
                          >
                            Price Drops as More People Order
                          </Text>
                          <span className="option-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              fill="none"
                            >
                              <path
                                d="M11.2491 4.00018C11.2491 3.84549 11.1876 3.69714 11.0783 3.58777C10.9689 3.47839 10.8205 3.41694 10.6658 3.41694C10.5112 3.41694 10.3628 3.47839 10.2534 3.58777C10.144 3.69714 10.0826 3.84549 10.0826 4.00018V5.34164L7.60965 2.42543C7.52037 2.32107 7.39712 2.25156 7.26162 2.22916C7.12612 2.20675 6.98706 2.23288 6.86893 2.30295L4.37849 3.82521L1.78307 0.710696C1.73405 0.65172 1.67389 0.602976 1.60603 0.567248C1.53818 0.53152 1.46395 0.509507 1.38758 0.502466C1.31122 0.495426 1.23422 0.503495 1.16097 0.526214C1.08773 0.548932 1.01967 0.585855 0.960696 0.634874C0.901719 0.683893 0.852976 0.744049 0.817248 0.811906C0.78152 0.879763 0.759507 0.953993 0.752466 1.03036C0.745426 1.10672 0.753495 1.18372 0.776214 1.25697C0.798932 1.33022 0.835855 1.39827 0.884874 1.45725L3.80108 4.9567C3.88988 5.06345 4.01384 5.13502 4.15069 5.15854C4.28754 5.18206 4.42829 5.15599 4.54763 5.08501L7.04391 3.58608L9.37687 6.33315H7.74963C7.59494 6.33315 7.44659 6.3946 7.33722 6.50397C7.22784 6.61335 7.16639 6.7617 7.16639 6.91639C7.16639 7.07107 7.22784 7.21942 7.33722 7.3288C7.44659 7.43818 7.59494 7.49963 7.74963 7.49963H10.6658C10.7376 7.49787 10.8086 7.48407 10.8758 7.4588L10.9575 7.41214C10.9879 7.39682 11.0171 7.37927 11.0449 7.35965C11.0769 7.3308 11.1044 7.29739 11.1266 7.2605C11.1461 7.23465 11.1637 7.20735 11.1791 7.17885C11.1934 7.14731 11.2032 7.11394 11.2083 7.0797C11.2319 7.02827 11.2457 6.97289 11.2491 6.91639V4.00018Z"
                                fill="#474B6D"
                              />
                            </svg>
                          </span>
                        </div>
                        <Text
                          as="p"
                          className={`${styleClass}__pricing-model-description option-description`}
                        >
                          Pick if you want cheaper merch the more you order.
                        </Text>
                      </div>
                    </label>

                    {/* Price Stays Same Card */}
                    <label
                      className={`${styleClass}__pricing-model-card store-pricing-option price-same ${
                        selectedPricingModel === 'same'
                          ? `${styleClass}__pricing-model-card--selected-pink`
                          : ''
                      } ${
                        isPricingReadOnly
                          ? `${styleClass}__pricing-model-card--disabled${
                              selectedPricingModel === 'same'
                                ? ` ${styleClass}__pricing-model-card--selected-disabled`
                                : ` ${styleClass}__pricing-model-card--readonly`
                            }`
                          : ''
                      }`}
                      onClick={() =>
                        !isPricingReadOnly && handlePricingModelSelect(groupIndex, 'same')
                      }
                    >
                      <div
                        className={`${styleClass}__pricing-model-radio-container pricing-radio-button`}
                      >
                        <input
                          type="radio"
                          name={`pricingModel-${groupIndex}`}
                          checked={selectedPricingModel === 'same'}
                          onChange={() => handlePricingModelSelect(groupIndex, 'same')}
                          id={`pricing-same-${groupIndex}`}
                          className={`${styleClass}__pricing-model-radio`}
                          disabled={isPricingReadOnly}
                        />
                        <label
                          htmlFor={`pricing-same-${groupIndex}`}
                          className={`${styleClass}__pricing-model-radio-label`}
                        />
                      </div>
                      <div
                        className={`${styleClass}__store-pricing-option-content store-pricing-option-content`}
                      >
                        <div className={`${styleClass}__pricing-model-header option-header`}>
                          <Text
                            as="h3"
                            className={`${styleClass}__pricing-model-title option-title`}
                          >
                            Price Stays the Same
                          </Text>
                          <span className="option-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="13"
                              height="10"
                              viewBox="0 0 13 10"
                              fill="none"
                            >
                              <path
                                d="M1.3584 4.41016L12.1284 4.47865"
                                stroke="#474B6D"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9.68942 6.9508L12.1287 4.47859L9.66346 2.02007"
                                stroke="#474B6D"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                        <Text
                          as="p"
                          className={`${styleClass}__pricing-model-description option-description`}
                        >
                          Pick if you only want one price.
                        </Text>
                      </div>
                    </label>
                  </div>
                )}

                {/* Select Minimum Section - Hide for permanent stores */}
                {storeType !== 'permanent-store' && selectedPricingModel && (
                  <div
                    className={`${styleClass}__version-b-minimum-section store-pricing-section__design-group-breakpoints`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0',
                      width: '100%',
                    }}
                  >
                    <h3
                      className={`${styleClass}__version-b-section-title`}
                      style={{
                        color: '#474B6D',
                        fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal',
                        margin: 0,
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      Select Minimum for {group.name}
                    </h3>
                    <p
                      className={`${styleClass}__version-b-description`}
                      style={{
                        fontSize: '14px',
                        color: '#474B6D',
                        margin: 0,
                        lineHeight: '1.5',
                        marginBottom: '16px',
                      }}
                    >
                      {getStoreDesignMinimumSubtitle(selectedMinimum)}
                    </p>

                    {/* Quantity Pills */}
                    <div
                      className={`${styleClass}__version-b-quantity-pills`}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                      }}
                    >
                      {isMoqPending && (
                        <div
                          className={`${styleClass}__version-b-moq-shimmer`}
                          aria-busy="true"
                          aria-label={STORE_CART_MOQ_LOADING_ARIA_LABEL}
                        />
                      )}
                      {!isMoqPending &&
                        quantities.map((qty) => {
                          const isSelected = qty === selectedMinimum;
                          return (
                            <button
                              key={qty}
                              type="button"
                              onClick={() => {
                                if (isPricingInteractionEnabled) {
                                  markUserSelectedGroupMinimum(groupIndex, qty);
                                  setSelectedMinimums({ ...selectedMinimums, [groupIndex]: qty });
                                }
                              }}
                              disabled={isPricingReadOnly}
                              className={`${styleClass}__version-b-pill ${
                                isSelected ? `${styleClass}__version-b-pill--selected` : ''
                              } ${
                                selectedPricingModel === 'drops'
                                  ? `${styleClass}__version-b-pill--purple`
                                  : `${styleClass}__version-b-pill--pink`
                              } ${
                                isPricingReadOnly ? `${styleClass}__version-b-pill--disabled` : ''
                              }`}
                              style={
                                isPricingReadOnly
                                  ? { cursor: 'not-allowed', opacity: 0.6 }
                                  : undefined
                              }
                            >
                              {qty}
                            </button>
                          );
                        })}
                    </div>

                    {/* Price Per Item Section */}
                    <div
                      className={`${styleClass}__version-b-price-section`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        width: '100%',
                        marginTop: '40px',
                      }}
                    >
                      <div
                        className={`${styleClass}__version-b-price-header`}
                        style={
                          isMobile
                            ? {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                width: '100%',
                                gap: '8px',
                              }
                            : {
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                width: '100%',
                                gap: '24px',
                              }
                        }
                      >
                        <h4
                          className={`${styleClass}__version-b-price-title`}
                          style={{
                            color: '#474B6D',
                            fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: 'normal',
                            margin: 0,
                          }}
                        >
                          Price Per Item
                        </h4>
                        {selectedPricingModel !== 'same' && (
                          <button
                            type="button"
                            className={`${styleClass}__version-b-price-toggle`}
                            onClick={() => openBreakdownModal(groupIndex)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '14px',
                              color: '#4A90E2',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                              outline: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            See Price Per Breakpoint
                          </button>
                        )}
                      </div>

                      {/* Product Cards */}
                      <div className={`${styleClass}__version-b-products-grid`}>
                        {productPriceGroups.map((productGroup) => {
                          const item = productGroup.representativeItem;
                          const calculatedPrice = getCalculatedItemPrice(item);
                          const itemIndexInGroup = group.items.findIndex(
                            (groupItem) => groupItem.id === item.id
                          );
                          const donationKey = `donation-${groupIndex}-${
                            itemIndexInGroup >= 0 ? itemIndexInGroup : 0
                          }`;
                          const productKey = `${groupIndex}-${
                            itemIndexInGroup >= 0 ? itemIndexInGroup : 0
                          }`;
                          const donationAmount = donationAmounts[donationKey] || '';
                          const finalPrice = getItemFinalPriceWithDonation(
                            donationAmount,
                            calculatedPrice ?? 0
                          );
                          const showsPerItemAdjustment =
                            storeType === 'fundraiser' || storeType === 'brand-drop';
                          const perItemAdjustmentLabel =
                            getStoreReviewPerItemAdjustmentLabel(resolvedStoreType);

                          return (
                            <div
                              key={productGroup.key}
                              className={`${styleClass}__version-b-product-card${
                                showsPerItemAdjustment
                                  ? ` ${styleClass}__version-b-product-card--fundraiser`
                                  : ''
                              }`}
                            >
                              <div className={`${styleClass}__version-b-product-card-main`}>
                                <div className={`${styleClass}__version-b-product-media`}>
                                  <div className={`${styleClass}__version-b-product-image`}>
                                    <img
                                      src={
                                        item.primaryImage ||
                                        item.frontImage ||
                                        '/src/assets/svg/image_error.svg'
                                      }
                                      alt={productGroup.apparelName || 'Product'}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = '/src/assets/svg/image_error.svg';
                                      }}
                                    />
                                  </div>
                                  {productGroup.colorSwatches.length > 0 && (
                                    <div className={`${styleClass}__version-b-color-swatches`}>
                                      {productGroup.colorSwatches.map((swatch) => (
                                        <span
                                          key={`${productGroup.key}-${swatch.hex}-${swatch.colorName}`}
                                          className={`${styleClass}__version-b-color-swatch`}
                                          style={{ background: swatch.hex }}
                                          title={swatch.colorName}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className={`${styleClass}__version-b-product-details`}>
                                  <p className={`${styleClass}__version-b-product-name`}>
                                    {productGroup.apparelName || 'Product Name'}
                                  </p>
                                  <div className={`${styleClass}__version-b-product-price-stack`}>
                                    <div className={`${styleClass}__version-b-product-price-row`}>
                                      {calculatedPrice == null ? (
                                        <span
                                          className={`${styleClass}__version-b-moq-shimmer`}
                                          aria-busy="true"
                                          aria-label={STORE_CART_PRICE_LOADING_ARIA_LABEL}
                                        />
                                      ) : (
                                        <>
                                          <span
                                            className={`${styleClass}__version-b-product-price`}
                                          >
                                            {currencyFormat(calculatedPrice)}
                                          </span>
                                          <span
                                            className={`${styleClass}__version-b-product-price-unit`}
                                          >
                                            {STORE_REVIEW_PRICE_PER_ITEM_UNIT_SUFFIX}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {showsPerItemAdjustment && (
                                <>
                                  <hr
                                    className={`${styleClass}__version-b-product-card-divider`}
                                    aria-hidden="true"
                                  />
                                  <div className={`${styleClass}__version-b-product-card-donation`}>
                                    <Text
                                      as="label"
                                      className={`${styleClass}__version-b-product-card-donation-label`}
                                      size={14}
                                      color="#474B6D"
                                      weight="regular"
                                    >
                                      {perItemAdjustmentLabel}
                                    </Text>
                                    <div
                                      className={`${styleClass}__version-b-product-card-donation-field`}
                                    >
                                      <Input
                                        name={`version-b-donation-${productKey}`}
                                        label=""
                                        type="number"
                                        min={STORE_DONATION_MARKUP_AMOUNT_LIMITS.MIN}
                                        max={STORE_DONATION_MARKUP_AMOUNT_LIMITS.MAX}
                                        step="0.01"
                                        value={donationAmount}
                                        placeholder="0"
                                        onKeyDown={preventStoreDonationMarkupNegativeKey}
                                        onChange={(_e, data) => {
                                          if (!isPricingReadOnly) {
                                            handleDonationAmountChange(
                                              donationKey,
                                              getReviewOrderItemId(item),
                                              data.value
                                            );
                                          }
                                        }}
                                        onBlur={() => {
                                          if (!isPricingReadOnly) {
                                            handleDonationAmountBlur(
                                              donationKey,
                                              getReviewOrderItemId(item),
                                              donationAmount
                                            );
                                          }
                                        }}
                                        containerClass={`${styleClass}__version-b-product-card-donation-input`}
                                        contentBefore="$"
                                        contentAfter={STORE_REVIEW_DONATION_INPUT_UNIT_SUFFIX}
                                        variant="large"
                                        disabled={isPricingReadOnly}
                                      />
                                    </div>
                                  </div>
                                  <div
                                    className={`${styleClass}__version-b-product-card-final-price`}
                                  >
                                    <Text
                                      as="span"
                                      className={`${styleClass}__version-b-product-card-final-price-label`}
                                      size={14}
                                      color="#474B6D"
                                      weight="regular"
                                    >
                                      {STORE_REVIEW_FINAL_PRICE_LABEL}
                                    </Text>
                                    <div
                                      className={`${styleClass}__version-b-product-card-final-price-value-row`}
                                    >
                                      <span
                                        className={`${styleClass}__version-b-product-card-final-price-value`}
                                      >
                                        {currencyFormat(finalPrice)}
                                      </span>
                                      <span
                                        className={`${styleClass}__version-b-product-price-unit`}
                                      >
                                        {STORE_REVIEW_PRICE_PER_ITEM_UNIT_SUFFIX}
                                      </span>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render individual design group (unused - keeping for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  // Render Pricing Breakdown Modal
  const renderPricingBreakdownModal = () => {
    if (!showBreakdownModal.isOpen || showBreakdownModal.groupIndex === null) return null;

    const { groupIndex } = showBreakdownModal;
    const group = designGroups[groupIndex];
    if (!group) return null;

    const designGroupMoq = getDesignGroupMoq(group);
    const designGroupMoqApiStatus = getDesignGroupMoqApiStatus(group);
    const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(designGroupMoqApiStatus);
    const { quantities } = getStoreReviewPricingBreakpoints(
      group,
      hasMoqTiers ? designGroupMoq : undefined,
      { savedBreakpointsOnly: isOnEditPage }
    );

    // Get the selected minimum for this group
    const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
    const selectedMinimum = selectedMinimums[groupIndex] ?? defaultMinimum;

    // Filter quantities to show only those >= selected minimum
    const filteredQuantities = isOnEditPage
      ? quantities
      : quantities.filter((qty) => qty >= selectedMinimum);
    const getBreakdownItemWithOverlay = (cartItem: ICartItemDetail): ICartItemDetail => {
      const overlay = breakdownPricingByItemId[cartItem.id];

      return overlay ? { ...cartItem, ...overlay } : cartItem;
    };

    const getBreakdownItemUnitPrice = (item: ICartItemDetail) =>
      getStoreReviewBreakpointUnitPrice(selectedMinimum, getBreakdownItemWithOverlay(item)) ?? 0;
    const breakdownProductGroups = groupStoreReviewItemsByProduct(
      group.items,
      getBreakdownItemUnitPrice
    );
    const includeDonationInBreakpointPrices =
      storeType === 'fundraiser' || storeType === 'brand-drop';

    const getBreakdownPriceForQuantity = (
      cartItem: ICartItemDetail,
      qty: number,
      donationAmount: string
    ): number | null => {
      const basePrice = getStoreReviewBreakpointUnitPrice(
        qty,
        getBreakdownItemWithOverlay(cartItem)
      );

      if (basePrice == null) {
        return null;
      }

      return getStoreReviewBreakpointDisplayPrice(
        basePrice,
        donationAmount,
        includeDonationInBreakpointPrices
      );
    };

    const renderBreakdownPrice = (price: number | null): React.ReactNode => {
      if (isBreakdownPricingLoading && price == null) {
        return (
          <span
            className={`${styleClass}__version-b-moq-shimmer`}
            aria-busy="true"
            aria-label={STORE_CART_PRICE_LOADING_ARIA_LABEL}
          />
        );
      }

      if (price == null) {
        return '—';
      }

      return currencyFormat(price);
    };

    const getProductGroupDonationAmount = (
      productGroup: (typeof breakdownProductGroups)[number]
    ) => {
      const itemIndexInGroup = group.items.findIndex(
        (groupItem) => groupItem.id === productGroup.representativeItem.id
      );
      const donationKey = `donation-${groupIndex}-${itemIndexInGroup >= 0 ? itemIndexInGroup : 0}`;

      return donationAmounts[donationKey] || '';
    };

    const breakdownOverlayBaseProps = {
      isOpen: true as const,
      disableScrollLock: true,
      overlayClassName: `${styleClass}__breakdown-modal-backdrop`,
      closeIconPath: undefined,
      closeIconWrapperStyles: { display: 'none' },
      onCloseClick: closeBreakdownModal,
      onBackdropClick: closeBreakdownModal,
      overlayStyles: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        opacity: 1,
      },
    };

    const renderBreakdownModalHeader = (headerModifierClass = ''): React.ReactNode => (
      <div className={`${styleClass}__breakdown-modal-header ${headerModifierClass}`.trim()}>
        <button
          type="button"
          className={`${styleClass}__breakdown-modal-back-btn`}
          onClick={closeBreakdownModal}
          aria-label="Go back"
        >
          <StoreCheckoutBackIcon />
        </button>
        <h2 className={`${styleClass}__breakdown-modal-title`}>
          Minimum and Breakpoints for {group.name}
        </h2>
      </div>
    );

    // Mobile & Tablet: Full-page view
    if (isMobileOrTablet) {
      return (
        <Overlay
          {...breakdownOverlayBaseProps}
          modalClassName={`${styleClass}__breakdown-modal ${styleClass}__breakdown-modal--mobile`}
          modalStyles={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: 'none',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            minWidth: '100vw',
            borderRadius: 0,
            padding: 0,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            background: '#fbfaff',
            zIndex: 9999,
          }}
        >
          {renderBreakdownModalHeader()}

          <div
            className={`${styleClass}__breakdown-modal-body breakdown-modal-scroll-area`}
            onScroll={handleBreakdownModalScroll}
          >
            {/* Products with Pricing Breakpoints */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {breakdownProductGroups.map((productGroup) => {
                const item = productGroup.representativeItem;
                const productGroupDonationAmount = getProductGroupDonationAmount(productGroup);

                return (
                  <div key={productGroup.key}>
                    {/* Card Container with Padding and Border */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid #E9E9F0',
                        background: '#FFF',
                      }}
                    >
                      {/* Mobile: Single Column Layout | Tablet: Two Column Layout */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: isMobile ? '16px' : '24px',
                          alignItems: isMobile ? 'flex-start' : 'flex-start',
                        }}
                      >
                        {/* Column 1: Product Image with Color Swatches */}
                        <div
                          className={
                            isMobile
                              ? `${styleClass}__breakdown-modal-item-image-column`
                              : undefined
                          }
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            alignItems: isMobile ? 'flex-start' : 'flex-start',
                            flexShrink: 0,
                            width: isMobile ? '100%' : 'auto',
                          }}
                        >
                          <div
                            className={
                              isMobile ? `${styleClass}__breakdown-modal-item-image` : undefined
                            }
                            style={{
                              width: isMobile ? '100%' : '140px',
                              maxWidth: isMobile ? '100%' : '140px',
                              height: isMobile ? undefined : '140px',
                              borderRadius: '12px',
                              border: '1px solid #DFEBF9',
                              background: '#FFF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px',
                              boxSizing: 'border-box',
                            }}
                          >
                            <img
                              src={
                                item.primaryImage ||
                                item.frontImage ||
                                '/src/assets/svg/image_error.svg'
                              }
                              alt="Product"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/src/assets/svg/image_error.svg';
                              }}
                            />
                          </div>
                          {/* Color Swatches Below Image */}
                          {productGroup.colorSwatches.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                paddingLeft: '4px',
                                alignSelf: 'flex-start',
                                width: isMobile ? '100%' : 'auto',
                                maxWidth: isMobile ? '100%' : '140px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {productGroup.colorSwatches.map((swatch) => (
                                <span
                                  key={`${productGroup.key}-${swatch.hex}-${swatch.colorName}`}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1.5px solid #E9E9F0',
                                    background: swatch.hex,
                                    flexShrink: 0,
                                  }}
                                  title={swatch.colorName}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Column 2: Product Name + Pricing Table + Donation Message */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            flex: 1,
                            width: isMobile ? '100%' : 'auto',
                            minWidth: 0,
                          }}
                        >
                          <p className={`${styleClass}__breakdown-modal-item-name`}>
                            {productGroup.apparelName || 'Product Name'}
                          </p>

                          {/* Pricing Breakpoints - Mobile: 4 per row (wraps to second row) | Tablet: horizontal scroll */}
                          <div
                            className={
                              isMobile
                                ? `${styleClass}__breakdown-modal-breakpoints-grid mobile-breakpoints-scroll`
                                : 'mobile-breakpoints-scroll'
                            }
                            style={
                              isMobile
                                ? {
                                    width: '100%',
                                    overflowX: 'visible',
                                    overflowY: 'visible',
                                    paddingBottom: '8px',
                                  }
                                : {
                                    display: 'flex',
                                    gap: '12px',
                                    justifyContent: 'flex-start',
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    paddingBottom: '8px',
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                  }
                            }
                          >
                            {filteredQuantities.map((qty, idx) => {
                              const price = getBreakdownPriceForQuantity(
                                item,
                                qty,
                                productGroupDonationAmount
                              );

                              return (
                                <div
                                  key={qty}
                                  className={
                                    isMobile
                                      ? `${styleClass}__breakdown-modal-breakpoint-col`
                                      : undefined
                                  }
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '6px',
                                    minWidth: isMobile ? 0 : '80px',
                                    flexShrink: isMobile ? undefined : 0,
                                  }}
                                >
                                  {/* Quantity */}
                                  <div
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      color: '#474B6D',
                                      fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                      lineHeight: '1',
                                    }}
                                  >
                                    {qty}
                                  </div>

                                  {/* Price */}
                                  <div
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      color: '#474B6D',
                                      fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                      lineHeight: '1',
                                    }}
                                  >
                                    {renderBreakdownPrice(price)}
                                  </div>

                                  {/* Label with Bar for All Columns */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '6px',
                                      alignItems: 'flex-start',
                                    }}
                                  >
                                    {/* Rectangle Bar */}
                                    <div
                                      className={
                                        isMobile
                                          ? `${styleClass}__breakdown-modal-breakpoint-bar`
                                          : undefined
                                      }
                                      style={{
                                        width: isMobile ? undefined : '70px',
                                        height: '6px',
                                        borderRadius: '30px',
                                        background: '#EAF8F4',
                                      }}
                                    />
                                    {/* Label Text */}
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#474B6D',
                                        fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                        lineHeight: '1',
                                        textAlign: 'left',
                                      }}
                                    >
                                      {idx === 0 ? 'Minimum' : `Breakpoint ${idx}`}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Donation/Markup Message (if fundraiser or brand-drop) */}
                          {productGroup.items.map((groupItem) => {
                            const itemIndex = group.items.findIndex(
                              (designGroupItem) => designGroupItem.id === groupItem.id
                            );
                            const donationKey = `donation-${groupIndex}-${
                              itemIndex >= 0 ? itemIndex : 0
                            }`;
                            const donationAmount = donationAmounts[donationKey];

                            if (
                              !(storeType === 'fundraiser' || storeType === 'brand-drop') ||
                              !hasPositiveDonationAmount(donationAmount)
                            ) {
                              return null;
                            }

                            return (
                              <div
                                key={donationKey}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '10px',
                                  padding: '12px 16px',
                                  background:
                                    storeType === 'brand-drop'
                                      ? '#FAFFFE'
                                      : STORE_FLOW_PRIMARY_BACKGROUND,
                                  borderRadius: '8px',
                                  border:
                                    storeType === 'brand-drop'
                                      ? '1px dashed #CCE9E1'
                                      : `1px dashed ${STORE_FLOW_PRIMARY_BORDER}`,
                                }}
                              >
                                {storeType === 'brand-drop' ? (
                                  <span
                                    style={{ fontSize: '18px', lineHeight: '1', marginTop: '2px' }}
                                  >
                                    💸
                                  </span>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="17"
                                    viewBox="0 0 21 17"
                                    fill="none"
                                    style={{ flexShrink: 0, marginTop: '2px' }}
                                  >
                                    <path
                                      d="M20.0028 9.44286C19.6996 9.21059 19.3466 9.05202 18.9716 8.97966C20.2976 7.47146 20.9688 5.96154 20.9688 4.4688C20.9688 2.00497 18.9845 5.12252e-05 16.5464 5.12252e-05C15.3723 -0.00557919 14.2436 0.453142 13.4062 1.27622C12.5689 0.453142 11.4402 -0.00557919 10.2661 5.12252e-05C7.82805 5.12252e-05 5.84375 2.00497 5.84375 4.4688C5.84375 5.34107 6.0775 6.21333 6.55016 7.10708C6.17288 7.26278 5.83025 7.49178 5.54211 7.78083L3.69531 9.62505H1.71875C1.26291 9.62505 0.825738 9.80613 0.50341 10.1285C0.181082 10.4508 0 10.888 0 11.3438L0 14.7813C0 15.2371 0.181082 15.6743 0.50341 15.9966C0.825738 16.319 1.26291 16.5001 1.71875 16.5001H10.3125C10.3968 16.5001 10.4808 16.4897 10.5626 16.4691L16.0626 15.0941C16.1152 15.0812 16.1666 15.064 16.2164 15.0426L19.5525 13.6211C19.5723 13.6134 19.5912 13.6048 19.6101 13.5954C19.9832 13.4087 20.3027 13.1302 20.5384 12.786C20.7742 12.4418 20.9185 12.0433 20.9578 11.628C20.9971 11.2127 20.93 10.7942 20.763 10.4119C20.5959 10.0296 20.3343 9.69616 20.0028 9.44286ZM10.2661 2.06255C10.7307 2.05538 11.1869 2.18697 11.5764 2.44048C11.9658 2.69399 12.2708 3.0579 12.4523 3.48568C12.53 3.67482 12.6622 3.83659 12.8321 3.95045C13.0019 4.0643 13.2018 4.12509 13.4062 4.12509C13.6107 4.12509 13.8106 4.0643 13.9804 3.95045C14.1503 3.83659 14.2825 3.67482 14.3602 3.48568C14.5417 3.0579 14.8467 2.69399 15.2361 2.44048C15.6256 2.18697 16.0818 2.05538 16.5464 2.06255C17.8252 2.06255 18.9062 3.16427 18.9062 4.4688C18.9062 6.02083 17.6172 7.80661 15.1783 9.64138L14.7812 9.73333C14.7812 9.69724 14.7812 9.66115 14.7812 9.62505C14.7812 8.89571 14.4915 8.19623 13.9758 7.68051C13.4601 7.16478 12.7606 6.87505 12.0312 6.87505H8.82664C8.20016 5.99505 7.90625 5.22247 7.90625 4.4688C7.90625 3.16427 8.98734 2.06255 10.2661 2.06255ZM2.0625 11.6876H3.09375V14.4376H2.0625V11.6876ZM18.7069 11.74L15.4834 13.115L10.1853 14.4376H5.15625V11.086L7.00047 9.24177C7.0958 9.1454 7.20929 9.0689 7.33438 9.01668C7.45947 8.96446 7.59367 8.93757 7.72922 8.93755H12.0312C12.2136 8.93755 12.3885 9.00998 12.5174 9.13892C12.6463 9.26785 12.7188 9.44271 12.7188 9.62505C12.7188 9.80739 12.6463 9.98226 12.5174 10.1112C12.3885 10.2401 12.2136 10.3126 12.0312 10.3126H9.625C9.3515 10.3126 9.08919 10.4212 8.8958 10.6146C8.7024 10.808 8.59375 11.0703 8.59375 11.3438C8.59375 11.6173 8.7024 11.8796 8.8958 12.073C9.08919 12.2664 9.3515 12.3751 9.625 12.3751H12.375C12.4528 12.3751 12.5303 12.3664 12.6062 12.3493L18.364 11.025L18.4044 11.0147C18.4631 10.9987 18.5247 10.9965 18.5844 11.0081C18.6441 11.0198 18.7003 11.0451 18.7487 11.082C18.797 11.1189 18.8362 11.1664 18.8633 11.2209C18.8903 11.2754 18.9044 11.3354 18.9045 11.3962C18.905 11.4659 18.8869 11.5344 18.8522 11.5948C18.8174 11.6552 18.7673 11.7053 18.7069 11.74Z"
                                      fill={STORE_FLOW_PRIMARY_COLOR}
                                    />
                                  </svg>
                                )}
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    color:
                                      storeType === 'brand-drop'
                                        ? '#32A687'
                                        : STORE_FLOW_PRIMARY_COLOR,
                                    fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                    lineHeight: '1.4',
                                  }}
                                >
                                  {storeType === 'brand-drop'
                                    ? `You'll receive ${currencyFormat(
                                        parseFloat(donationAmount)
                                      )} from every purchase`
                                    : getFundraiserDonationPurchaseMessage(
                                        currencyFormat(parseFloat(donationAmount)),
                                        charityName
                                      )}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Overlay>
      );
    }

    // Desktop: Side Modal
    return (
      <Overlay
        {...breakdownOverlayBaseProps}
        modalClassName={`${styleClass}__breakdown-modal`}
        modalStyles={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 'auto',
          transform: 'none',
          width: 'max-content',
          minWidth: 'unset',
          maxWidth: '95vw',
          background: '#FBFAFF',
          boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
          zIndex: 9999,
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
          boxSizing: 'border-box',
          borderTopLeftRadius: '20px',
          borderBottomLeftRadius: '20px',
        }}
      >
        {renderBreakdownModalHeader(`${styleClass}__breakdown-modal-header--desktop`)}

        <div
          className={`${styleClass}__breakdown-modal-content breakdown-modal-scroll-area`}
          onScroll={handleBreakdownModalScroll}
        >
          {/* Products with Pricing Breakpoints */}
          <div className={`${styleClass}__breakdown-modal-products`}>
            {breakdownProductGroups.map((productGroup) => {
              const item = productGroup.representativeItem;
              const productGroupDonationAmount = getProductGroupDonationAmount(productGroup);

              return (
                <div key={productGroup.key}>
                  <div className={`${styleClass}__breakdown-modal-product-card`}>
                    <div className={`${styleClass}__breakdown-modal-product-row`}>
                      {/* Column 1: Product Image with Color Swatches */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          alignItems: 'flex-start',
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: '140px',
                            height: '140px',
                            borderRadius: '12px',
                            border: '1px solid #DFEBF9',
                            background: '#FFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            boxSizing: 'border-box',
                          }}
                        >
                          <img
                            src={
                              item.primaryImage ||
                              item.frontImage ||
                              '/src/assets/svg/image_error.svg'
                            }
                            alt="Product"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/src/assets/svg/image_error.svg';
                            }}
                          />
                        </div>
                        {/* Color Swatches Below Image */}
                        {productGroup.colorSwatches.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              paddingLeft: '4px',
                              flexWrap: 'wrap',
                            }}
                          >
                            {productGroup.colorSwatches.map((swatch) => (
                              <span
                                key={`${productGroup.key}-${swatch.hex}-${swatch.colorName}`}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  border: '1.5px solid #E9E9F0',
                                  background: swatch.hex,
                                  flexShrink: 0,
                                }}
                                title={swatch.colorName}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={`${styleClass}__breakdown-modal-product-details`}>
                        <p className={`${styleClass}__breakdown-modal-item-name`}>
                          {productGroup.apparelName || 'Product Name'}
                        </p>

                        <div
                          className={`${styleClass}__breakdown-modal-breakpoints-row desktop-breakpoints-scroll`}
                        >
                          {filteredQuantities.map((qty, idx) => {
                            const price = getBreakdownPriceForQuantity(
                              item,
                              qty,
                              productGroupDonationAmount
                            );

                            return (
                              <div
                                key={qty}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: '8px',
                                  minWidth: '103px',
                                  flexShrink: 0,
                                }}
                              >
                                {/* Quantity */}
                                <div
                                  style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#474B6D',
                                    fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                    lineHeight: '1',
                                  }}
                                >
                                  {qty}
                                </div>

                                {/* Price */}
                                <div
                                  style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#474B6D',
                                    fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                    lineHeight: '1',
                                  }}
                                >
                                  {renderBreakdownPrice(price)}
                                </div>

                                {/* Label with Bar for All Columns */}
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  {/* Rectangle Bar */}
                                  <div
                                    style={{
                                      width: '103px',
                                      height: '7px',
                                      borderRadius: '30px',
                                      background: '#EAF8F4',
                                    }}
                                  />
                                  {/* Label Text */}
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#474B6D',
                                      fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                      lineHeight: '1',
                                      textAlign: 'left',
                                    }}
                                  >
                                    {idx === 0 ? 'Minimum' : `Breakpoint ${idx}`}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Donation/Markup Message (if fundraiser or brand-drop) */}
                        {productGroup.items.map((groupItem) => {
                          const itemIndex = group.items.findIndex(
                            (designGroupItem) => designGroupItem.id === groupItem.id
                          );
                          const donationKey = `donation-${groupIndex}-${
                            itemIndex >= 0 ? itemIndex : 0
                          }`;
                          const donationAmount = donationAmounts[donationKey];

                          if (
                            !(storeType === 'fundraiser' || storeType === 'brand-drop') ||
                            !hasPositiveDonationAmount(donationAmount)
                          ) {
                            return null;
                          }

                          return (
                            <div
                              key={donationKey}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '12px 16px',
                                background:
                                  storeType === 'brand-drop'
                                    ? '#FAFFFE'
                                    : STORE_FLOW_PRIMARY_BACKGROUND,
                                borderRadius: '8px',
                                border:
                                  storeType === 'brand-drop'
                                    ? '1px dashed #CCE9E1'
                                    : `1px dashed ${STORE_FLOW_PRIMARY_BORDER}`,
                              }}
                            >
                              {storeType === 'brand-drop' ? (
                                <span
                                  style={{ fontSize: '18px', lineHeight: '1', marginTop: '2px' }}
                                >
                                  💸
                                </span>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="21"
                                  height="17"
                                  viewBox="0 0 21 17"
                                  fill="none"
                                  style={{ flexShrink: 0, marginTop: '2px' }}
                                >
                                  <path
                                    d="M20.0028 9.44286C19.6996 9.21059 19.3466 9.05202 18.9716 8.97966C20.2976 7.47146 20.9688 5.96154 20.9688 4.4688C20.9688 2.00497 18.9845 5.12252e-05 16.5464 5.12252e-05C15.3723 -0.00557919 14.2436 0.453142 13.4062 1.27622C12.5689 0.453142 11.4402 -0.00557919 10.2661 5.12252e-05C7.82805 5.12252e-05 5.84375 2.00497 5.84375 4.4688C5.84375 5.34107 6.0775 6.21333 6.55016 7.10708C6.17288 7.26278 5.83025 7.49178 5.54211 7.78083L3.69531 9.62505H1.71875C1.26291 9.62505 0.825738 9.80613 0.50341 10.1285C0.181082 10.4508 0 10.888 0 11.3438L0 14.7813C0 15.2371 0.181082 15.6743 0.50341 15.9966C0.825738 16.319 1.26291 16.5001 1.71875 16.5001H10.3125C10.3968 16.5001 10.4808 16.4897 10.5626 16.4691L16.0626 15.0941C16.1152 15.0812 16.1666 15.064 16.2164 15.0426L19.5525 13.6211C19.5723 13.6134 19.5912 13.6048 19.6101 13.5954C19.9832 13.4087 20.3027 13.1302 20.5384 12.786C20.7742 12.4418 20.9185 12.0433 20.9578 11.628C20.9971 11.2127 20.93 10.7942 20.763 10.4119C20.5959 10.0296 20.3343 9.69616 20.0028 9.44286ZM10.2661 2.06255C10.7307 2.05538 11.1869 2.18697 11.5764 2.44048C11.9658 2.69399 12.2708 3.0579 12.4523 3.48568C12.53 3.67482 12.6622 3.83659 12.8321 3.95045C13.0019 4.0643 13.2018 4.12509 13.4062 4.12509C13.6107 4.12509 13.8106 4.0643 13.9804 3.95045C14.1503 3.83659 14.2825 3.67482 14.3602 3.48568C14.5417 3.0579 14.8467 2.69399 15.2361 2.44048C15.6256 2.18697 16.0818 2.05538 16.5464 2.06255C17.8252 2.06255 18.9062 3.16427 18.9062 4.4688C18.9062 6.02083 17.6172 7.80661 15.1783 9.64138L14.7812 9.73333C14.7812 9.69724 14.7812 9.66115 14.7812 9.62505C14.7812 8.89571 14.4915 8.19623 13.9758 7.68051C13.4601 7.16478 12.7606 6.87505 12.0312 6.87505H8.82664C8.20016 5.99505 7.90625 5.22247 7.90625 4.4688C7.90625 3.16427 8.98734 2.06255 10.2661 2.06255ZM2.0625 11.6876H3.09375V14.4376H2.0625V11.6876ZM18.7069 11.74L15.4834 13.115L10.1853 14.4376H5.15625V11.086L7.00047 9.24177C7.0958 9.1454 7.20929 9.0689 7.33438 9.01668C7.45947 8.96446 7.59367 8.93757 7.72922 8.93755H12.0312C12.2136 8.93755 12.3885 9.00998 12.5174 9.13892C12.6463 9.26785 12.7188 9.44271 12.7188 9.62505C12.7188 9.80739 12.6463 9.98226 12.5174 10.1112C12.3885 10.2401 12.2136 10.3126 12.0312 10.3126H9.625C9.3515 10.3126 9.08919 10.4212 8.8958 10.6146C8.7024 10.808 8.59375 11.0703 8.59375 11.3438C8.59375 11.6173 8.7024 11.8796 8.8958 12.073C9.08919 12.2664 9.3515 12.3751 9.625 12.3751H12.375C12.4528 12.3751 12.5303 12.3664 12.6062 12.3493L18.364 11.025L18.4044 11.0147C18.4631 10.9987 18.5247 10.9965 18.5844 11.0081C18.6441 11.0198 18.7003 11.0451 18.7487 11.082C18.797 11.1189 18.8362 11.1664 18.8633 11.2209C18.8903 11.2754 18.9044 11.3354 18.9045 11.3962C18.905 11.4659 18.8869 11.5344 18.8522 11.5948C18.8174 11.6552 18.7673 11.7053 18.7069 11.74Z"
                                    fill={STORE_FLOW_PRIMARY_COLOR}
                                  />
                                </svg>
                              )}
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: '13px',
                                  color:
                                    storeType === 'brand-drop'
                                      ? '#32A687'
                                      : STORE_FLOW_PRIMARY_COLOR,
                                  fontFamily: STORE_FLOW_BODY_FONT_FAMILY,
                                  lineHeight: '1.4',
                                }}
                              >
                                {storeType === 'brand-drop'
                                  ? `You'll receive ${currencyFormat(
                                      parseFloat(donationAmount)
                                    )} from every purchase`
                                  : getFundraiserDonationPurchaseMessage(
                                      currencyFormat(parseFloat(donationAmount)),
                                      charityName
                                    )}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Overlay>
    );
  };

  return (
    <div className={styleClass}>
      <StoreReviewProgressiveModal
        isOpen={progressiveModalOpen && progressiveModalSource != null}
        presetStyleId={progressiveModalSource ?? 'ai-flat-lay'}
        isMobile={isMobileOrTablet}
        isReadOnly={isReviewStepReadOnly}
        singleImagePanel={isSingleImageGenerationVariation}
        simulateBackSlotFailure={isErrorStateVariation}
        onClose={closeProgressiveStyleModal}
        onImagesCommitted={onProgressiveImagesCommitted}
      />

      {/* Pricing Breakdown Modal */}
      {renderPricingBreakdownModal()}

      {/* Render all design groups - Always use Version B */}
      {designGroups.map((group, index) => {
        const userOrg = user?.accountType !== 'corporate' ? user?.organization ?? '' : '';
        const isGroupBlockedForCheckout = group.items.some((groupItem) => {
          const rejection =
            groupItem.designRejectionInfo ??
            isDesignRejected(
              groupItem.fromDesignTool,
              groupItem.designLicensing,
              groupItem.designPricing,
              userOrg
            );
          return rejection.isDesignSelfBlockedForCheckout || rejection.isDesignLicensingRejected;
        });

        return (
          <div
            key={index}
            id={index === 0 ? 'store-review-first-design-group' : undefined}
            className={`${styleClass}__design-group`}
          >
            {/* Header */}
            <div className={`${styleClass}__header`}>
              <Text as="div" className={`${styleClass}__header__title`}>
                {group.name}
              </Text>
              {!isReviewStepReadOnly && (
                <ToolTip title="Remove Design" placement="top">
                  <button
                    type="button"
                    className={`${styleClass}__header__close`}
                    onClick={(event) => handleRemoveDesignGroup(index, event)}
                    aria-label="Remove design group"
                  >
                    <Icon source="/src/assets/svg/remove.svg" width="24px" height="24px" />
                  </button>
                </ToolTip>
              )}
            </div>

            {/* Render all products in this design group */}
            {group.items.map((item, itemIdx) => (
              <React.Fragment key={`product-${index}-${itemIdx}`}>
                {renderProductCard(item, itemIdx, index)}
                {itemIdx < group.items.length - 1 && (
                  <div className={`${styleClass}__item-separator`} />
                )}
              </React.Fragment>
            ))}

            {/* Version B Layout — greyed/disabled when any item is blocked */}
            <div
              className={
                isGroupBlockedForCheckout ? `${styleClass}__version-b-wrapper--blocked` : undefined
              }
            >
              {renderVersionB(group, index)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

StoreReviewOrderStep.displayName = 'StoreReviewOrderStep';

export default StoreReviewOrderStep;
